import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../../../../redux/store";
import { ScanToContext } from "..";
import {
    ScanToMobilePreview,
    BannerPreviewData,
    OffresPreviewData,
    ActionsPreviewData,
    PublicitesPreviewData,
    CommandePreviewData,
    DiversPreviewData,
    SocialChannel,
} from "../View";

import { PropsType, LANGUAGES, DEFAULT_RESTAURANT_TEXTS, DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from "./types";
import { DEFAULT_SOCIAL_CHANNELS } from "./constants";
import {
    Container,
    LeftSection,
    RightSection,
    SectionTitle,
    PreviewToggleButton,
    ClosePreviewButton,
    OpenInNewWindowButton,
    PreviewOverlay,
} from "./styled";
import { ColorsCard } from "./ColorsCard";
import { RestaurantInfoCard } from "./RestaurantInfoCard";
import { SocialNetworksCard } from "./SocialNetworksCard";
import { LegalInfoCard } from "./LegalInfoCard";
import { LanguagesCard } from "./LanguagesCard";

// ─── Component ──────────────────────────────────────────

export function PiedDePage({ rowData }: PropsType): JSX.Element {
    const { t } = useTranslation();
    const mainColor =
        useSelector(
            (state: RootState) => state.global.infoUser.maincolor
        ) || DEFAULT_PRIMARY_COLOR;
    const selectedLanguage = useSelector(
        (state: RootState) => state.global.infoUser.currentLanguage
    ) || "fr";
    const {
        sharedBannerData,
        sharedOffresData,
        sharedActionsData,
        sharedPublicitesData,
        sharedCommandeData,
        sharedPaiementData,
        sharedAppelServeurData,
        setSharedDiversData,
        broadcastChannelRef,
    } = React.useContext(ScanToContext);

    // ─── State ──────────────────────────────────────────
    // Cast: MiscellaneousConfig type doesn't declare every UI-state field used
    // at runtime (selectedLangRestaurant, restaurantTexts, etc.). Out of scope
    // for ARCH-02.
    const savedDivers = useSelector(
        (state: RootState) => state.scanTo?.miscellaneousSettings
    ) as any;
    const [primaryColor, setPrimaryColor] = React.useState(savedDivers?.primaryColor || DEFAULT_PRIMARY_COLOR);
    const [secondaryColor, setSecondaryColor] = React.useState(savedDivers?.secondaryColor || DEFAULT_SECONDARY_COLOR);
    const [showPrimaryPicker, setShowPrimaryPicker] = React.useState(false);
    const [showSecondaryPicker, setShowSecondaryPicker] = React.useState(false);

    // Restaurant info - multilingual
    const [selectedLangRestaurant, setSelectedLangRestaurant] = React.useState(savedDivers?.selectedLangRestaurant || "fr");
    const [restaurantTexts, setRestaurantTexts] = React.useState<Record<string, { name: string; description: string }>>(
        savedDivers?.restaurantTexts || JSON.parse(JSON.stringify(DEFAULT_RESTAURANT_TEXTS))
    );
    const [nameVisible, setNameVisible] = React.useState(savedDivers?.nameVisible ?? true);
    const [descriptionVisible, setDescriptionVisible] = React.useState(savedDivers?.descriptionVisible ?? true);

    const currentTexts = restaurantTexts[selectedLangRestaurant] || { name: "", description: "" };
    const currentDefaults = DEFAULT_RESTAURANT_TEXTS[selectedLangRestaurant] || { name: "", description: "" };

    const [showSocialNetworks, setShowSocialNetworks] = React.useState(savedDivers?.showSocialNetworks ?? true);
    const [socialChannels, setSocialChannels] = React.useState<SocialChannel[]>(() => {
        const saved = savedDivers?.socialChannels;
        if (saved?.length) {
            // API returns { id, url, enabled } — normalize to { key, label, url, enabled }
            return saved.map((ch: any) => ({
                key: ch.key || ch.id || "",
                label: ch.label || DEFAULT_SOCIAL_CHANNELS.find((d) => d.key === (ch.key || ch.id))?.label || ch.key || ch.id || "",
                url: ch.url || "",
                enabled: ch.enabled ?? false,
            }));
        }
        return DEFAULT_SOCIAL_CHANNELS.map((ch) => ({ ...ch }));
    });
    const [showLegalInfo, setShowLegalInfo] = React.useState(savedDivers?.showLegalInfo ?? true);

    // Language settings
    const [enabledLanguages, setEnabledLanguages] = React.useState<Record<string, boolean>>(
        savedDivers?.fullEnabledLanguages || Object.fromEntries(LANGUAGES.map((l) => [l.code, true]))
    );
    const [defaultLanguage, setDefaultLanguage] = React.useState<string>(savedDivers?.defaultLanguage || "fr");

    const [previewVisible, setPreviewVisible] = React.useState(true);

    // ─── Build preview data and sync ────────────────────
    const buildPreviewData = React.useCallback((): DiversPreviewData => {
        const texts = restaurantTexts[selectedLangRestaurant] || { name: "", description: "" };
        return {
            primaryColor,
            secondaryColor,
            restaurantName: texts.name,
            restaurantDescription: texts.description,
            nameVisible,
            descriptionVisible,
            socialChannels: showSocialNetworks ? socialChannels : socialChannels.map((ch) => ({ ...ch, enabled: false })),
            showLegalInfo,
            enabledLanguages: Object.entries(enabledLanguages)
                .filter(([, enabled]) => enabled)
                .map(([code]) => code),
            defaultLanguage,
        };
    }, [primaryColor, secondaryColor, restaurantTexts, selectedLangRestaurant, nameVisible, descriptionVisible, socialChannels, showSocialNetworks, showLegalInfo, enabledLanguages, defaultLanguage]);

    React.useEffect(() => {
        const previewData = {
            ...buildPreviewData(),
            // Full config for Redux persistence
            selectedLangRestaurant,
            restaurantTexts,
            showSocialNetworks,
            fullEnabledLanguages: enabledLanguages,
        };

        // Share with other tabs via context
        setSharedDiversData(previewData);

        // Send to external preview window via the centralized BroadcastChannel
        if (broadcastChannelRef.current) {
            try {
                broadcastChannelRef.current.postMessage({
                    source: "divers",
                    ...previewData,
                });
            } catch (_) {
                /* channel closed */
            }
        }

        // Persist for external window initial load
        sessionStorage.setItem("scanToDiversPreviewData", JSON.stringify(previewData));
    }, [buildPreviewData, broadcastChannelRef, setSharedDiversData, restaurantTexts, showSocialNetworks, enabledLanguages, selectedLangRestaurant]);

    // ─── Handlers ───────────────────────────────────────
    const handleResetColors = () => {
        setPrimaryColor(DEFAULT_PRIMARY_COLOR);
        setSecondaryColor(DEFAULT_SECONDARY_COLOR);
    };

    const handleRestaurantTextChange = (field: "name" | "description", value: string) => {
        setRestaurantTexts((prev) => ({
            ...prev,
            [selectedLangRestaurant]: { ...prev[selectedLangRestaurant], [field]: value },
        }));
    };

    const handleResetRestaurantField = (field: "name" | "description") => {
        const defaults = DEFAULT_RESTAURANT_TEXTS[selectedLangRestaurant];
        if (defaults) {
            setRestaurantTexts((prev) => ({
                ...prev,
                [selectedLangRestaurant]: { ...prev[selectedLangRestaurant], [field]: defaults[field] },
            }));
        }
    };

    const handleResetAllRestaurant = () => {
        setRestaurantTexts(JSON.parse(JSON.stringify(DEFAULT_RESTAURANT_TEXTS)));
        setNameVisible(true);
        setDescriptionVisible(true);
    };

    const handleToggleSocial = (key: string) => {
        setSocialChannels((prev) =>
            prev.map((ch) => (ch.key === key ? { ...ch, enabled: !ch.enabled } : ch))
        );
    };

    const handleSocialUrlChange = (key: string, url: string) => {
        setSocialChannels((prev) =>
            prev.map((ch) => (ch.key === key ? { ...ch, url } : ch))
        );
    };

    const handleToggleLanguage = (code: string) => {
        setEnabledLanguages((prev) => {
            const newState = { ...prev, [code]: !prev[code] };
            // If disabling the default language, switch default to first enabled
            if (!newState[code] && code === defaultLanguage) {
                const firstEnabled = LANGUAGES.find((l) => newState[l.code] && l.code !== code);
                if (firstEnabled) {
                    setDefaultLanguage(firstEnabled.code);
                }
            }
            // Ensure at least one language stays enabled
            const enabledCount = Object.values(newState).filter(Boolean).length;
            if (enabledCount === 0) {
                return prev;
            }
            return newState;
        });
    };

    const handleSetDefaultLanguage = (code: string) => {
        // Enable the language if it's not enabled
        if (!enabledLanguages[code]) {
            setEnabledLanguages((prev) => ({ ...prev, [code]: true }));
        }
        setDefaultLanguage(code);
    };

    const handleOpenInNewWindow = () => {
        const previewData = buildPreviewData();
        sessionStorage.setItem("scanToDiversPreviewData", JSON.stringify(previewData));

        const width = 768;
        const height = 1024;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        window.open(
            "/scan-to-preview",
            "ScanToPreview",
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
    };

    // ─── Current diversData for local preview ───────────
    const diversData: DiversPreviewData = buildPreviewData();

    return (
        <Container data-testid="scan-to-misc-container">
            <LeftSection data-testid="scan-to-misc-left-section">
                <SectionTitle>{t("Miscellaneous settings")}</SectionTitle>

                {/* Card 1 - Colors */}
                <ColorsCard
                    mainColor={mainColor}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    showPrimaryPicker={showPrimaryPicker}
                    showSecondaryPicker={showSecondaryPicker}
                    onTogglePrimaryPicker={() => setShowPrimaryPicker(!showPrimaryPicker)}
                    onToggleSecondaryPicker={() => setShowSecondaryPicker(!showSecondaryPicker)}
                    onPrimaryChange={setPrimaryColor}
                    onSecondaryChange={setSecondaryColor}
                    onClosePrimaryPicker={() => setShowPrimaryPicker(false)}
                    onCloseSecondaryPicker={() => setShowSecondaryPicker(false)}
                    onResetColors={handleResetColors}
                />

                {/* Card 2 - Restaurant Information */}
                <RestaurantInfoCard
                    mainColor={mainColor}
                    selectedLangRestaurant={selectedLangRestaurant}
                    onSelectLangRestaurant={setSelectedLangRestaurant}
                    currentTexts={currentTexts}
                    currentDefaults={currentDefaults}
                    nameVisible={nameVisible}
                    descriptionVisible={descriptionVisible}
                    onRestaurantTextChange={handleRestaurantTextChange}
                    onToggleNameVisible={() => setNameVisible(!nameVisible)}
                    onToggleDescriptionVisible={() => setDescriptionVisible(!descriptionVisible)}
                    onResetRestaurantField={handleResetRestaurantField}
                    onResetAllRestaurant={handleResetAllRestaurant}
                />

                {/* Card 3 - Social Networks */}
                <SocialNetworksCard
                    mainColor={mainColor}
                    showSocialNetworks={showSocialNetworks}
                    onToggleShowSocialNetworks={() => setShowSocialNetworks(!showSocialNetworks)}
                    socialChannels={socialChannels}
                    onToggleSocial={handleToggleSocial}
                    onSocialUrlChange={handleSocialUrlChange}
                />

                {/* Card 4 - Legal Information */}
                <LegalInfoCard
                    mainColor={mainColor}
                    showLegalInfo={showLegalInfo}
                    onToggleShowLegalInfo={() => setShowLegalInfo(!showLegalInfo)}
                />

                {/* Card 5 - Languages */}
                <LanguagesCard
                    mainColor={mainColor}
                    enabledLanguages={enabledLanguages}
                    defaultLanguage={defaultLanguage}
                    onToggleLanguage={handleToggleLanguage}
                    onSetDefaultLanguage={handleSetDefaultLanguage}
                />
            </LeftSection>

            <PreviewToggleButton
                data-testid="scan-to-misc-preview-toggle-btn"
                mainColor={mainColor}
                onClick={() => setPreviewVisible(!previewVisible)}
                title={previewVisible ? t("Hide preview") : t("Show preview")}
            >
                {previewVisible ? (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                )}
            </PreviewToggleButton>

            <PreviewOverlay isVisible={previewVisible} onClick={() => setPreviewVisible(false)} />

            <RightSection data-testid="scan-to-misc-preview-section" isVisible={previewVisible}>
                <ClosePreviewButton data-testid="scan-to-misc-close-preview-btn" onClick={() => setPreviewVisible(false)} title={t("Close preview")}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                </ClosePreviewButton>
                <OpenInNewWindowButton
                    data-testid="scan-to-misc-open-new-window-btn"
                    mainColor={mainColor}
                    onClick={handleOpenInNewWindow}
                    title={t("Open in new window")}
                >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
                    </svg>
                </OpenInNewWindowButton>
                <ScanToMobilePreview
                    mainColor={mainColor}
                    selectedLanguage={selectedLanguage}
                    bannerData={sharedBannerData as BannerPreviewData | null}
                    offresData={sharedOffresData as OffresPreviewData | null}
                    actionsData={sharedActionsData as ActionsPreviewData | null}
                    publicitesData={sharedPublicitesData as PublicitesPreviewData | null}
                    commandeData={sharedCommandeData as CommandePreviewData | null}
                    diversData={diversData}
                    paiementData={sharedPaiementData}
                    appelServeurData={sharedAppelServeurData}
                />
            </RightSection>
        </Container>
    );
}
